import type { SelectOption } from '~/shared/utils/types';

/**
 * How one filter dimension's pending value is read and updated from a checkbox option key.
 * The 10 per-dimension filter components this replaced differed only in their codec.
 */
export interface ValueCodec<V> {
  has(value: V, optionKey: string): boolean;
  toggle(value: V, optionKey: string): V;
  clear(): V;
  count(value: V): number;
}

/** Dimensions whose pending value is a plain list of option keys. */
export const stringListCodec: ValueCodec<string[]> = {
  has: (value, key) => value.includes(key),
  toggle: (value, key) =>
    value.includes(key) ? value.filter((v) => v !== key) : [...value, key],
  clear: () => [],
  count: (value) => value.length,
};

/** Dimensions whose pending value is a list of `SelectOption`s in a fixed group. */
export function selectOptionCodec(group: SelectOption['group']): ValueCodec<SelectOption[]> {
  return {
    has: (value, key) => value.some((o) => o.id === key),
    toggle: (value, key) =>
      value.some((o) => o.id === key)
        ? value.filter((o) => o.id !== key)
        : [...value, { id: key, name: key, group }],
    clear: () => [],
    count: (value) => value.length,
  };
}

/**
 * Tags are `SelectOption`s too, but their group is derived per-option from the part of the
 * key before the FIRST colon (`env: prod` → group `tag:env`), matching the id encoding that
 * `filterConverters` round-trips through `ExplorationFilter`.
 */
export const tagOptionCodec: ValueCodec<SelectOption[]> = {
  has: (value, key) => value.some((o) => o.id === key),
  toggle: (value, key) => {
    if (value.some((o) => o.id === key)) return value.filter((o) => o.id !== key);
    const colonIdx = key.indexOf(':');
    const tagKey = (colonIdx >= 0 ? key.slice(0, colonIdx) : key).trim();
    return [...value, { id: key, name: key, group: `tag:${tagKey}` as SelectOption['group'] }];
  },
  clear: () => [],
  count: (value) => value.length,
};

/**
 * A codec with its value type erased, so the dimension registry can hold codecs over
 * different value types in one array. The single unchecked cast lives in `eraseCodec`, and is
 * sound because each dimension pairs its codec with the matching `stateKey`.
 */
export interface ErasedValueCodec {
  has(value: unknown, optionKey: string): boolean;
  toggle(value: unknown, optionKey: string): unknown;
  clear(): unknown;
  count(value: unknown): number;
}

export function eraseCodec<V>(codec: ValueCodec<V>): ErasedValueCodec {
  return codec as ErasedValueCodec;
}
